import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { TableProps } from '@mui/material/Table';
import { TableSortLabelTypeMap } from '@mui/material/TableSortLabel';
import { OverflowMenuItem } from 'shared/components/overflow-menu/overflow-menu';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import RowItem from 'shared/components/table/types/row-item';
import { descendingCaseInsensitiveStringComparator, descendingComparator } from 'shared/components/table/utils/comparator-utils';
import { NestedKeyof, ResolveByNestedKey } from 'shared/components/table/utils/nested-keyof-utils';

export type TableOverflowMenuItem<DataType> = {
  label: string;
  action?: (item: DataType) => void;
  disabled?: (item: DataType) => boolean;
  isVisible?: (item: DataType) => boolean;
};

type PaginationProps = {
  count: number;
  onPageChanged: (skip: number) => void;
  rowsPerPage: number;
  page: number;
  onRowsPerPageChanged: (rowsPerPage: number) => void;
};

export type Props<DataType extends object, Key extends NestedKeyof<DataType>> = {
  data: RowItem<DataType>[];
  currentItem?: DataType;
  columns: ColumnDefinition<DataType, Key>[];
  onItemClick?: (item: DataType) => void;
  rowsAreSelectable?: boolean;
  onSelectedRowsChanged?: (items: DataType[]) => void;
  selectedRows?: RowItem<DataType>[];
  onSelectAllRowsChanged?: (checked: boolean) => void;
  pagination?: PaginationProps;
  rowIdentifier: (item: DataType) => string;

  onRowSelected?: (item: DataType) => void;
  onRowDeselected?: (item: DataType) => void;
  className?: string;
  overflowMenuOptions?: Array<TableOverflowMenuItem<DataType>>;
  onSortChanged?: (property: Key, isAscending: boolean) => void;
  useBuiltInSorter?: boolean;
  initialOrderBy?: Key;
  initialOrderDirection?: TableSortLabelTypeMap['props']['direction'];
  sx?: TableProps['sx'];
  errorMessage?: string;
  getErrorMessageForRow?: (data: DataType) => string | undefined;
  customFirstRow?: React.ReactNode;
  disabled?: boolean;
};

export type Table<DataType extends object, Key extends NestedKeyof<DataType>> = {
  orderBy: Key;
  orderDirection: TableSortLabelTypeMap['props']['direction'];
  isOverflowMenuOpen: boolean;
  anchorEl: HTMLElement | null;
  clickedRow: DataType | null;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  selectedRowsState: {
    state: RowItem<DataType>[];
    setSelectedRowsState: Dispatch<SetStateAction<RowItem<DataType>[]>>;
  };
  handleRequestSort: (property: Key) => void;
  getData: () => RowItem<DataType>[];
  mapMenuItems: (menuItems: Array<TableOverflowMenuItem<DataType>>, rowData: DataType) => (OverflowMenuItem | 'divider')[];
  mapMenuItem: (item: TableOverflowMenuItem<DataType>, rowData: DataType) => OverflowMenuItem | 'divider';
  onMoreClick: (event: React.MouseEvent<HTMLElement>, row: RowItem<DataType>) => void;
  closeOverflowMenu: () => void;
  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectAllRowsChanged?: (checked: boolean) => void;
};

export const useTable = <DataType extends object, Key extends NestedKeyof<DataType>>({
  data,
  columns,
  onSelectAllRowsChanged,
  onSortChanged,
  pagination,
  useBuiltInSorter,
  initialOrderBy,
  initialOrderDirection,
}: Readonly<Props<DataType, Key>>): Table<DataType, Key> => {
  const [orderBy, setOrderBy] = useState<Key>(initialOrderBy ?? columns[0].key);
  const [orderDirection, setOrderDirection] = useState<TableSortLabelTypeMap['props']['direction']>(initialOrderDirection ?? 'asc');
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [clickedRow, setClickedRow] = useState<DataType | null>(null);
  const [selectedRowsState, setSelectedRowsState] = useState<RowItem<DataType>[]>([]);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const handleRequestSort = (property: Key): void => {
    const isCurrentlyAscending = orderBy === property && orderDirection === 'asc';
    onSortChanged?.(property, !isCurrentlyAscending);
    setOrderDirection(isCurrentlyAscending ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparator = (
    direction: TableSortLabelTypeMap['props']['direction'],
    by: Key,
  ): ((a: RowItem<DataType>, b: RowItem<DataType>) => number) | undefined => {
    const orderByNestedKey = `data.${by}` as NestedKeyof<RowItem<DataType>>;
    return direction === 'desc'
      ? (a: RowItem<DataType>, b: RowItem<DataType>): number =>
          typeof ResolveByNestedKey<RowItem<DataType>>(orderByNestedKey, a) === 'string'
            ? descendingCaseInsensitiveStringComparator(a, b, orderByNestedKey)
            : descendingComparator(a, b, orderByNestedKey)
      : (a: RowItem<DataType>, b: RowItem<DataType>): number =>
          typeof ResolveByNestedKey<RowItem<DataType>>(orderByNestedKey, a) === 'string'
            ? -descendingCaseInsensitiveStringComparator(a, b, orderByNestedKey)
            : -descendingComparator(a, b, orderByNestedKey);
  };

  const getData = (): RowItem<DataType>[] => (useBuiltInSorter ? [...data].sort(getComparator(orderDirection, orderBy)) : data);

  const menuItemIsVisible = (rowData: DataType, isVisible?: ((item: DataType) => boolean) | undefined): boolean =>
    isVisible ? isVisible(rowData) : true;

  const mapMenuItems = (menuItems: Array<TableOverflowMenuItem<DataType>>, rowData: DataType): (OverflowMenuItem | 'divider')[] =>
    menuItems
      .filter((item) => (typeof item === 'string' ? true : menuItemIsVisible(rowData, item.isVisible)))
      .map((item) => mapMenuItem(item, rowData));

  const mapMenuItem = (item: TableOverflowMenuItem<DataType>, rowData: DataType): OverflowMenuItem | 'divider' =>
    item.label === 'divider'
      ? 'divider'
      : {
          label: item.label,
          action: (): void => item.action?.(rowData),
          disabled: item.disabled?.(rowData) ?? false,
        };

  const onMoreClick = (event: React.MouseEvent<HTMLElement>, row: RowItem<DataType>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setIsOverflowMenuOpen(true);
    setClickedRow(row.data);
  };

  const closeOverflowMenu = (): void => {
    setIsOverflowMenuOpen(false);
    setClickedRow(null);
  };

  const handleChangePage = (_: unknown, newPage: number): void => {
    tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    pagination?.onPageChanged(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void =>
    pagination?.onRowsPerPageChanged(Number.parseInt(event.target.value));

  return {
    orderBy,
    orderDirection,
    isOverflowMenuOpen,
    anchorEl,
    clickedRow,
    tableContainerRef: tableContainerRef as React.RefObject<HTMLDivElement>,
    selectedRowsState: {
      state: selectedRowsState,
      setSelectedRowsState,
    },
    handleRequestSort,
    getData,
    mapMenuItems,
    mapMenuItem,
    onMoreClick,
    closeOverflowMenu,
    handleChangePage,
    handleChangeRowsPerPage,
    onSelectAllRowsChanged,
  };
};
